import { c as _c } from "react/compiler-runtime";
import { cn } from 'cn';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import type { ImportQueueProgress } from '../types/import-queue-progress';
interface ImportQueueProgressSummaryProps {
  progress: ImportQueueProgress;
  /** When set, the failed count becomes a link to the failed-only view. */
  onShowFailed?: () => void;
}
function count(value: number): string {
  return value.toLocaleString();
}

/**
 * Where a multi-file import stands, and whether the tab is
 * still needed. Uploads run in the browser, so the tab must
 * stay open until the last file is sent; processing after
 * that is server-side and survives the tab closing.
 */
export function ImportQueueProgressSummary(t0) {
  const $ = _c(46);
  const {
    progress,
    onShowFailed
  } = t0;
  const {
    total,
    uploading,
    processing,
    done,
    failed
  } = progress;
  const finished = done + failed;
  const isUploading = uploading > 0;
  const tabRequired = progress.tabRequired ?? isUploading;
  let t1;
  if ($[0] !== done || $[1] !== finished || $[2] !== isUploading || $[3] !== processing || $[4] !== total || $[5] !== uploading) {
    t1 = isUploading ? `Uploading ${count(total - uploading)} of ${count(total)}` : processing > 0 ? `Processing ${count(finished)} of ${count(total)}` : `${count(done)} of ${count(total)} imported`;
    $[0] = done;
    $[1] = finished;
    $[2] = isUploading;
    $[3] = processing;
    $[4] = total;
    $[5] = uploading;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const headline = t1;
  const note = tabRequired ? "Keep this tab open until every file has uploaded." : processing > 0 ? "All files are uploaded. You can close this tab \u2014 processing continues in the background." : null;
  let t2;
  if ($[7] !== finished || $[8] !== isUploading || $[9] !== total || $[10] !== uploading) {
    t2 = total === 0 ? 0 : Math.round((isUploading ? total - uploading : finished) / total * 100);
    $[7] = finished;
    $[8] = isUploading;
    $[9] = total;
    $[10] = uploading;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  const percent = t2;
  let t3;
  if ($[12] !== headline) {
    t3 = <p className="text-foreground text-sm font-semibold tracking-tight">{headline}</p>;
    $[12] = headline;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] !== percent) {
    t4 = <span className="text-muted-foreground text-xs tabular-nums">{percent}%</span>;
    $[14] = percent;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  let t5;
  if ($[16] !== t3 || $[17] !== t4) {
    t5 = <div className="flex items-baseline justify-between gap-2">{t3}{t4}</div>;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== percent) {
    t6 = <Progress value={percent} className="bg-muted h-1.5" aria-label="Import progress" data-sentry-element="Progress" data-sentry-source-file="import-queue-progress-summary.tsx" />;
    $[19] = percent;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== done) {
    t7 = count(done);
    $[21] = done;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  const t8 = `${t7} done`;
  let t9;
  if ($[23] !== processing) {
    t9 = processing > 0 ? `${count(processing)} processing` : null;
    $[23] = processing;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== isUploading || $[26] !== uploading) {
    t10 = isUploading ? `${count(uploading)} to upload` : null;
    $[25] = isUploading;
    $[26] = uploading;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== t10 || $[29] !== t8 || $[30] !== t9) {
    t11 = [t8, t9, t10].filter(Boolean);
    $[28] = t10;
    $[29] = t8;
    $[30] = t9;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  const t12 = t11.join(" \xB7 ");
  let t13;
  if ($[32] !== failed || $[33] !== onShowFailed) {
    t13 = failed > 0 ? <>{" \xB7 "}{onShowFailed ? <Button type="button" variant="link" className="text-destructive h-auto p-0 text-xs underline-offset-2" onClick={onShowFailed}>{`${count(failed)} failed`}</Button> : <span className="text-destructive">{`${count(failed)} failed`}</span>}</> : null;
    $[32] = failed;
    $[33] = onShowFailed;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== t12 || $[36] !== t13) {
    t14 = <p className="text-muted-foreground text-xs tabular-nums">{t12}{t13}</p>;
    $[35] = t12;
    $[36] = t13;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  let t15;
  if ($[38] !== note || $[39] !== tabRequired) {
    t15 = note ? <p className={cn("text-xs leading-snug", {
      "text-foreground font-medium": tabRequired,
      "text-muted-foreground": !tabRequired
    })}>{note}</p> : null;
    $[38] = note;
    $[39] = tabRequired;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  let t16;
  if ($[41] !== t14 || $[42] !== t15 || $[43] !== t5 || $[44] !== t6) {
    t16 = <div className="flex shrink-0 flex-col gap-2 px-3 pt-3 pb-2" aria-live="polite" data-sentry-component="ImportQueueProgressSummary" data-sentry-source-file="import-queue-progress-summary.tsx">{t5}{t6}{t14}{t15}</div>;
    $[41] = t14;
    $[42] = t15;
    $[43] = t5;
    $[44] = t6;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  return t16;
}